// Pure helpers for the scheduled-round → PlayPage prefill handoff (F-079).
//
// WHY THIS EXISTS — the field bug: tapping "Start round" on a scheduled round
// opened /play with NOTHING pre-populated (course, 9/18 + which-nine, and
// players all had to be re-entered). The prefill was carried ONLY in React
// Router's in-memory `location.state`. In the deployed PWA / Capacitor shell
// that transport does not reliably reach PlayPage's first render: the Home→
// /play navigation is exactly when F-074 applies a freshly-published, WAITING
// service worker (the owner field-tests right after publishing, so a new SW is
// always waiting on that first navigation), and the resulting page reload /
// WebView remount re-runs PlayPage's `useState(prefill?…)` after `location.
// state` is gone → prefill undefined → nothing transfers.
//
// The fix carries the prefill through sessionStorage as well (the same
// transport the codebase already trusts over location.state — see
// `tf_fresh_round`, `tf_resume_consumed`, `tf:preroundSkipped`), which survives
// the reload/remount. These pure mappers keep the prefill→state mapping, the
// roster (de)serialization, and the nine_set carry unit-testable without a DOM.

import { toDateInputValue, toTimeInputValue } from './leagueConfigForm'

export const PREFILL_STORAGE_KEY = 'tf:scheduledRoundPrefill'

// The canonical blank player row. MUST match PlayerSearchInput's row shape
// ({ name, phone, linked_player_id, handicap, linked_handicap, linked_birthdate })
// so an unfilled slot round-trips cleanly through search / link / handicap edits.
// linked_birthdate (F-100-T1 review fix) carries a linked friend's stored
// birthdate through the roster so PlayPage can feed PreRoundGamesPanel's
// seatBirthdates prop — enabling the pre-flight under-18 wager rejection for
// friends (not just seat 0, the logged-in user).
export function blankPlayer() {
  return { name: '', phone: '', linked_player_id: null, handicap: null, linked_handicap: null, linked_birthdate: null }
}

// Whether a player row carries a real selection (a typed name or a linked
// player) vs. an untouched blank slot.
export function playerRowFilled(row) {
  if (!row || typeof row !== 'object') return false
  return (typeof row.name === 'string' && row.name.trim() !== '') || row.linked_player_id != null
}

// Coerce one roster entry to the PlayerSearchInput row shape, tolerating
// partial / legacy shapes (older writes stored only name/phone/linked_player_id).
export function normalizePlayerRow(row) {
  if (!row || typeof row !== 'object') return blankPlayer()
  return {
    name: typeof row.name === 'string' ? row.name : '',
    phone: typeof row.phone === 'string' ? row.phone : '',
    linked_player_id: row.linked_player_id ?? null,
    handicap: row.handicap ?? null,
    linked_handicap: row.linked_handicap ?? null,
    // F-100-T1 review fix: preserve a linked friend's birthdate through
    // (de)serialization so schedule prefills, resize, and roster storage
    // don't drop the field the under-18 wager pre-flight depends on.
    linked_birthdate: row.linked_birthdate ?? null,
  }
}

// Serialize the in-memory otherPlayers array into the roster payload persisted
// on the scheduled round. Drops fully-blank slots (no name AND no link) so an
// untouched "3 players" schedule stores no empty rows, but KEEPS every slot the
// user actually filled.
export function serializeRoster(otherPlayers) {
  if (!Array.isArray(otherPlayers)) return []
  return otherPlayers.map(normalizePlayerRow).filter(playerRowFilled)
}

// Rebuild the otherPlayers array (length playerCount - 1) from a stored roster,
// padding with blanks and truncating to fit. Preserves as many stored entries
// as the seat count allows.
export function deserializeRoster(roster, playerCount) {
  const seats = Math.max(0, (Number(playerCount) || 1) - 1)
  const src = Array.isArray(roster) ? roster.map(normalizePlayerRow) : []
  const out = []
  for (let i = 0; i < seats; i++) out.push(src[i] || blankPlayer())
  return out
}

// Resize otherPlayers to n-1 seats, PRESERVING already-entered rows. The old
// updatePlayerCount padded with a PARTIAL shape (missing handicap fields) and
// truncated; this keeps the full shape and every filled name when the count is
// bumped up or down.
export function resizeOtherPlayers(prev, n) {
  const seats = Math.max(0, (Number(n) || 1) - 1)
  const src = Array.isArray(prev) ? prev : []
  const out = []
  for (let i = 0; i < seats; i++) out.push(src[i] ? normalizePlayerRow(src[i]) : blankPlayer())
  return out
}

// nine_set to PERSIST for a schedule / round: only meaningful for a 9-hole
// selection; anything else stores null. Normalizes to 'front' | 'back'.
export function nineSetForSave(holes, nineSet) {
  return String(holes) === '9' ? (nineSet === 'back' ? 'back' : 'front') : null
}

// The full initial round-setup state derived from a scheduled-round prefill.
// prefill may be null/undefined (a normal ad-hoc /play open) → sane defaults.
export function deriveRoundSetupFromPrefill(prefill) {
  const p = prefill || {}
  const playerCount = Number(p.player_count) > 0 ? Number(p.player_count) : 1
  const holes = p.holes ? String(p.holes) : '18'
  const nineSet = p.nine_set === 'back' ? 'back' : 'front'
  const otherPlayers = playerCount > 1 ? deserializeRoster(p.roster, playerCount) : []
  // Reveal the "Other Players" section up-front only when the schedule actually
  // carried named / linked players — otherwise keep the lean default.
  const addFriends = otherPlayers.some(playerRowFilled)
  return { holes, nineSet, playerCount, otherPlayers, addFriends }
}

// Build the prefill object handed to PlayPage from a scheduled-round row (the
// GET /scheduled-rounds shape: id, course_id, holes, player_count, roster,
// nine_set). Kept pure so both the navigation and its unit test share one map.
export function buildPrefillFromRound(r) {
  if (!r || typeof r !== 'object') return null
  return {
    scheduled_round_id: r.id,
    course_id: r.course_id,
    holes: r.holes != null ? String(r.holes) : '18',
    player_count: r.player_count != null ? Number(r.player_count) : 1,
    nine_set: r.nine_set ?? null,
    roster: Array.isArray(r.roster) ? r.roster : [],
  }
}

// ── league round-start prefill (F-080) ──────────────────────────────────────
// League rounds speak `hole_set` ('front' | 'back' | 'full') + the course's
// hole count, NOT the holes/nine_set pair the scheduled-round path uses. These
// mappers translate a league_round into the SAME prefill shape PlayPage already
// consumes, so the existing deriveRoundSetupFromPrefill + course-by-id hydrate
// paths are reused unchanged. Kept as a SEPARATE mapper (not an overload of
// buildPrefillFromRound) so the scheduled-round path can never regress.

// hole_set (+ the course's hole count) → the holes / nine_set pair PlayPage
// wants. 'front' = holes 1-9 (front nine), 'back' = holes 10-18 (back nine),
// 'full' = the whole course: 9 holes on a 9-hole course, 18 on an 18-hole one.
// A 9-hole course's 'full' is its only nine, so nine_set='front' there; an
// 18-hole 'full' is an 18-hole round, where nine_set is meaningless (null).
export function holesFromHoleSet(holeSet, courseHoles) {
  const ch = Number(courseHoles)
  if (holeSet === 'front') return { holes: '9', nine_set: 'front' }
  if (holeSet === 'back')  return { holes: '9', nine_set: 'back' }
  // 'full' (or anything unrecognized) — derive from the course's hole count.
  // A missing/blank hole count (Number(null) === 0) is NOT a 9-hole course —
  // it's "unknown", which defaults to an 18-hole round (the common case).
  if (Number.isFinite(ch) && ch > 0 && ch <= 9) return { holes: '9', nine_set: 'front' }
  return { holes: '18', nine_set: null }
}

// Build the PlayPage prefill from a league_round row (the
// get_league_pairing_for_player shape: league_round_id, course_id, hole_set,
// course_holes). Carries league_round_id so the started round LINKS to league
// standings via the save path. player_count defaults to 1 — a league round is
// the player's own round; opponents score from their own devices.
export function buildPrefillFromLeagueRound(r) {
  if (!r || typeof r !== 'object') return null
  const lrId = r.league_round_id ?? r.id
  if (lrId == null) return null
  const { holes, nine_set } = holesFromHoleSet(r.hole_set, r.course_holes)
  return {
    league_round_id: lrId,
    course_id: r.course_id ?? null,
    holes,
    nine_set,
    player_count: 1,
    roster: [],
  }
}

// F-080 req 3 — a single one-off event ('event' type) pins its ONE course +
// which-nine as a league_rounds row at week_number=1 (the same schema-free
// storage a league uses per week; public.events grows no column). This is the
// pure selector for that row from an event admin view's league_rounds list.
// Kept here beside the other F-080 league_round mappers so the whole
// hole_set/league_round translation layer lives in one import-safe module.
export const SINGLE_EVENT_WEEK = 1
export function singleEventRoundFrom(rounds) {
  if (!Array.isArray(rounds)) return null
  return rounds.find(r => Number(r?.week_number) === SINGLE_EVENT_WEEK) || null
}

// F-080-T2 — a multi-day / multi-round OUTING ('outing' type) pins a course +
// which-nine PER ROUND using the SAME schema-free storage a league week uses:
// one league_rounds row per round, week_number = the round index
// (1..events.rounds_scheduled). public.events grows no column; the legacy
// per-day course columns have been retired (D-341 Phase-3 cutover) and are no
// longer read anywhere. These pure mappers keep the round selector + prefill
// build unit-testable
// without pulling EventDraftAdmin's supabase import chain.

// Select the league_rounds row for a given outing round index (week_number ==
// round index). Tolerates week_number arriving as a string (postgres.js can
// serialize an int either way — the same tolerance singleEventRoundFrom uses).
export function outingRoundFrom(rounds, roundIndex) {
  if (!Array.isArray(rounds)) return null
  const idx = Number(roundIndex)
  if (!Number.isFinite(idx)) return null
  return rounds.find(r => Number(r?.week_number) === idx) || null
}

// Build the PlayPage prefill from an outing round's league_rounds row (the
// get_event_admin_view.league_rounds shape: course_id, hole_set, course_holes).
// Deliberately does NOT set league_round_id — an outing is NOT a league, and
// stamping league_round_id would thread the saved round through league-standings
// machinery (round_scores.league_round_id → recomputeLeagueRoundPoints). We
// carry course + holes + which-nine only, reusing holesFromHoleSet so an
// outing round derives 9/18 + which-nine exactly like a league/single-event
// round. A round with no course yet still builds a prefill (course_id null),
// consistent with buildPrefillFromLeagueRound's unassigned-week behavior.
export function buildPrefillFromOutingRound(r) {
  if (!r || typeof r !== 'object') return null
  const { holes, nine_set } = holesFromHoleSet(r.hole_set, r.course_holes)
  return {
    course_id: r.course_id ?? null,
    holes,
    nine_set,
    player_count: 1,
    roster: [],
  }
}

// ── event_rounds selectors (B2T1) ────────────────────────────────────────────
// event_rounds is the schedule SOURCE OF TRUTH (round_number + which_nine),
// superseding the interim league_rounds storage (week_number + hole_set) the
// F-080 course cards read. These mirror singleEventRoundFrom / outingRoundFrom /
// buildPrefillFromOutingRound but key on `round_number` and read `which_nine`
// (the same 'front'|'back'|'full' vocabulary hole_set used). Kept pure so the
// admin cards' selection + prefill stay unit-testable without a DB.

export const SINGLE_EVENT_ROUND_NUMBER = 1

// The round_number=1 event_rounds row that holds a single one-off event's
// course. Tolerates round_number arriving as a string (postgres.js int
// serialization), exactly like singleEventRoundFrom did for week_number.
export function firstEventRoundFrom(rounds) {
  if (!Array.isArray(rounds)) return null
  return rounds.find(r => Number(r?.round_number) === SINGLE_EVENT_ROUND_NUMBER) || null
}

// The event_rounds row for a given round index (round_number == index). Mirror
// of outingRoundFrom for the new schedule table.
export function eventRoundFrom(rounds, roundIndex) {
  if (!Array.isArray(rounds)) return null
  const idx = Number(roundIndex)
  if (!Number.isFinite(idx)) return null
  return rounds.find(r => Number(r?.round_number) === idx) || null
}

// Build the PlayPage prefill from an event_rounds row (the
// get_event_admin_view.event_rounds shape: course_id, which_nine, course_holes).
// Like buildPrefillFromOutingRound this carries course + holes + which-nine only
// and deliberately does NOT stamp a league_round_id (an event/outing is not a
// league). The event_round_id → round-scores link is B2T3's cutover, not here.
export function buildPrefillFromEventRound(r) {
  if (!r || typeof r !== 'object') return null
  const { holes, nine_set } = holesFromHoleSet(r.which_nine, r.course_holes)
  return {
    course_id: r.course_id ?? null,
    holes,
    nine_set,
    player_count: 1,
    roster: [],
  }
}

// ── event_rounds schedule EDITOR helpers (B2T2) ──────────────────────────────
// The commissioner per-round schedule editor CRUDs event_rounds directly — each
// round carries round_number (order), play_date (ANY weekday, not just
// Fri/Sat/Sun), tee_time, course, which_nine, is_scramble. These pure helpers
// keep the sort / next-number / row→form / reorder math unit-testable without a
// DOM or a live DB (the sandbox has neither).

// event_rounds sorted ascending by round_number (numeric, tolerating the string
// serialization postgres.js can emit for an int). Returns a NEW array.
export function sortEventRounds(rounds) {
  const list = Array.isArray(rounds) ? rounds.slice() : []
  return list.sort((a, b) => (Number(a?.round_number) || 0) - (Number(b?.round_number) || 0))
}

// The round_number to assign a newly-added round: one past the current max, or 1
// when there are none yet (the empty-state "add first round" path — how the
// owner re-enters the lost schedules + the Masters-of-Mulligans draft).
export function nextEventRoundNumber(rounds) {
  const list = Array.isArray(rounds) ? rounds : []
  let max = 0
  for (const r of list) {
    const n = Number(r?.round_number)
    if (Number.isFinite(n) && n > max) max = n
  }
  return max + 1
}

// Map an event_rounds row into the editor's form shape. `course` is the
// { id, name, holes } object the CourseSearch picker + card display use (or null
// when unassigned); play_date / tee_time are coerced to the HTML date/time input
// shapes ('YYYY-MM-DD' / 'HH:MM'); which_nine defaults to 'full'.
export function eventRoundToForm(row) {
  const r = row && typeof row === 'object' ? row : {}
  return {
    round_number: Number(r.round_number) || null,
    course: r.course_id
      ? { id: r.course_id, name: r.course_name ?? null, holes: r.course_holes ?? null }
      : null,
    which_nine: r.which_nine || 'full',
    play_date: toDateInputValue(r.play_date),
    tee_time: toTimeInputValue(r.tee_time),
    is_scramble: !!r.is_scramble,
  }
}

// Compute the full ordered list of event_round ids after moving ONE round up or
// down by a single slot (direction: 'up' | 'down'). Returns the reordered id
// array to hand the reorder_event_rounds action, or null when the move is a
// no-op (already at the top/bottom, the round/id is unknown, or there are <2
// rounds). Pure: the swap math is decoupled from the network call so it stays
// testable, and the action renumbers 1..N in exactly this order.
export function reorderedEventRoundIds(rounds, roundNumber, direction) {
  const sorted = sortEventRounds(rounds).filter(r => r?.id != null)
  if (sorted.length < 2) return null
  const idx = sorted.findIndex(r => Number(r.round_number) === Number(roundNumber))
  if (idx === -1) return null
  const swapWith = direction === 'up' ? idx - 1 : direction === 'down' ? idx + 1 : -1
  if (swapWith < 0 || swapWith >= sorted.length) return null
  const ids = sorted.map(r => r.id)
  const tmp = ids[idx]
  ids[idx] = ids[swapWith]
  ids[swapWith] = tmp
  return ids
}

// ── pre-B2 league_rounds → event_rounds display fallback (B2T1 review fix) ───
// Before the B2T1 write cutover, the F-080 course cards persisted an event's /
// outing's per-round course into league_rounds (week_number = round index,
// hole_set = which-nine). A draft created that way has its course pins in
// league_rounds, NOT event_rounds, so after B2 deploys the cards (which now read
// event_rounds) would show "No course assigned" for every round and the pin
// would look lost. These helpers surface those legacy pins as a READ-TIME
// fallback so a returning organizer still sees their saved course. Saving always
// writes event_rounds via set_event_round, so the fallback naturally retires the
// moment the owner re-saves. which_nine and hole_set share the same
// 'front'|'back'|'full' vocabulary, so the field map is a straight rename.

// Map a league_rounds row (week_number + hole_set) into the event_rounds display
// shape (round_number + which_nine). Returns null for junk / a missing index.
export function leagueRoundAsEventRound(lr) {
  if (!lr || typeof lr !== 'object') return null
  const idx = Number(lr.week_number)
  if (!Number.isFinite(idx)) return null
  return {
    round_number: idx,
    course_id: lr.course_id ?? null,
    course_name: lr.course_name ?? null,
    course_holes: lr.course_holes ?? null,
    which_nine: lr.hole_set ?? 'full',
    play_date: null,
    tee_time: null,
    is_scramble: false,
  }
}

// Merge event_rounds (the B2T1 write source of truth) with a league_rounds
// read-time fallback. Per round index the event_rounds row wins; but when an
// event_rounds row lacks a course_id AND a matching league_rounds row DOES carry
// one, the course fields (and its which-nine) are filled from league_rounds so a
// pre-B2 pin still displays. Round indices present ONLY in league_rounds are
// included too. Display / hydration ONLY — the write path is unchanged.
export function mergeEventRounds(eventRounds, leagueRounds) {
  const ev = Array.isArray(eventRounds) ? eventRounds : []
  const lg = Array.isArray(leagueRounds) ? leagueRounds : []
  const byIdx = new Map()
  for (const r of ev) {
    const idx = Number(r?.round_number)
    if (Number.isFinite(idx)) byIdx.set(idx, { ...r })
  }
  for (const lr of lg) {
    const mapped = leagueRoundAsEventRound(lr)
    if (!mapped) continue
    const idx = mapped.round_number
    const existing = byIdx.get(idx)
    if (!existing) {
      byIdx.set(idx, mapped)
    } else if (!existing.course_id && mapped.course_id) {
      byIdx.set(idx, {
        ...existing,
        course_id: mapped.course_id,
        course_name: mapped.course_name,
        course_holes: mapped.course_holes,
        which_nine: mapped.which_nine ?? existing.which_nine,
      })
    }
  }
  return Array.from(byIdx.values())
    .sort((a, b) => Number(a.round_number) - Number(b.round_number))
}

// ── sessionStorage transport (thin, browser-guarded) ────────────────────────
// Persisted at navigation time in HomePage so it exists BEFORE any /play effect
// (incl. the F-074 SW-update reload) can fire, and read by PlayPage as the
// fallback when in-memory location.state has evaporated.

export function stashPrefill(prefill) {
  if (!prefill) return
  try { sessionStorage.setItem(PREFILL_STORAGE_KEY, JSON.stringify(prefill)) } catch { /* private mode / no storage */ }
}

export function readStashedPrefill() {
  try {
    const raw = sessionStorage.getItem(PREFILL_STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

export function clearStashedPrefill() {
  try { sessionStorage.removeItem(PREFILL_STORAGE_KEY) } catch { /* no-op */ }
}
